import { useRef, useState } from 'react';

import {
  ARC_MAX_SETPOINT,
  ARC_MIN_SETPOINT,
  cartesianToPolarAngle,
  center,
  clampNumber,
  disabledOverlayCircleRadius,
  distanceFromCenter,
  enlargedRadialMarkerRadius,
  getSetpointCoord,
  innerCircleRadius,
  isNearSetpoint,
  maxDragStartDistance,
  outerCircleRadius,
  pathForTemperatureArc,
  radialMarkerRadius,
  snapToStep,
  temperatureForAngle,
} from './radialGeometry.js';

// The draggable setpoint dial — a web port of app/src/components/Radial/index.js
// (the HALO25-267 rework).
//
// THE ARC IS FIXED AT 5–30 °C. Every bit of geometry — the coloured arc, the dot
// positions, the handle and the min/max labels — is drawn over ARC_MIN..ARC_MAX,
// so the dial reads identically for every zone. The zone's own minSetpoint /
// maxSetpoint ONLY clamp the value the user is allowed to select (clampSnap);
// they are still the slider's ARIA min/max (the selectable range).
//
// Visually it shows current-vs-target: a coloured arc up to the current room
// temperature, a ring of "climbing" dots bridging from there up to the target
// (rendered static here — the pulsing animation is a separate step), a white
// current-temperature dot and an accent (ice-blue in frost) target dot.
//
// Interaction (all snap to 50 centidegrees / 0.5 °C and clamp to the zone range):
//   - drag the handle around the arc (pointer),
//   - tap the upper/lower half of the inner circle to nudge ±0.5 °C,
//   - arrow keys (± step), Home / End (min / max) — the ARIA slider contract.
//
// OFFLINE is its own face, as in the app: when the zone cannot be reached the arc,
// the dots, the readout and the min/max labels all go, and the circle carries a
// single large "Offline" over the app's ring of accent dots (its LoadingRadial).
// Nothing about the last-reported target is drawn, because none of it is current.
//
// While a drag is in progress the dial shows a LOCAL value (instant feedback,
// independent of the redux round-trip) and only dispatches when the snapped value
// actually changes. The focusable slider is the wrapping element (role="slider");
// the SVG is pointer-transparent (CSS) and used only to convert pointer
// coordinates into the geometry's viewBox space via getScreenCTM.

const STEP = 50;

function formatCelsius(centi) {
  return (centi / 100).toFixed(1);
}

// Colour + subtext, mirroring the app's roomTemperatureColour: ice-blue at/below
// the frost floor, accent green otherwise. The app dropped the old neutral grey —
// the arc is always either frost-blue or green.
function toneFor(setpoint, frostThreshold) {
  if (setpoint <= frostThreshold) {
    return { tone: 'cold', subText: 'Frost protection' };
  }
  return { tone: 'accent', subText: 'Target temperature' };
}

// The app's LoadingRadial ring (Radial/index.js): 36 dots spread evenly along the
// arc, positioned by mapping 0…3600 in steps of 102 over the same 0…3600 range —
// copied so the offline face sits on exactly the app's geometry.
const OFFLINE_RING = (() => {
  const ts = [];
  for (let t = 0; t < 3600; t += 102) {
    ts.push(t);
  }
  return ts.map((t) => getSetpointCoord(t, 0, 3600));
})();

// Whole-degree centidegree values in [from, to) — the dot temperatures.
function degreeSteps(from, to) {
  const steps = [];
  for (let t = from; t < to; t += 100) {
    steps.push(t);
  }
  return steps;
}

export default function Radial({
  setpoint,
  roomTemperature = 1600,
  minSetpoint = 500,
  maxSetpoint = 3000,
  onSetpointChange,
  disabled = false,
  disabledReason,
  offline = false,
  label = 'Target temperature',
}) {
  // Offline implies inert: there is nothing to drag toward when the zone cannot
  // be reached. Every guard and the disabled styling read this, not `disabled`.
  const inert = disabled || offline;

  const svgRef = useRef(null);
  const drag = useRef({ dragging: false, downInside: false });
  // The value shown while actively dragging (null when not dragging); gives the
  // handle instant feedback without waiting on the store.
  const [dragValue, setDragValue] = useState(null);
  // The last value dispatched during the current drag, so we only fire a command
  // when the snapped setpoint changes.
  const lastCommittedRef = useRef(null);

  const displaySetpoint = dragValue ?? setpoint;

  // Selection is clamped to the ZONE range; the arc geometry below uses the fixed
  // 5–30 range instead.
  const clampSnap = (rawCenti) =>
    clampNumber(snapToStep(rawCenti), minSetpoint, maxSetpoint);

  // Discrete change (keyboard / tap): dispatch straight away.
  const commit = (rawCenti) => {
    if (inert || !onSetpointChange) {
      return;
    }
    onSetpointChange(clampSnap(rawCenti));
  };

  // Drag change: update the local value every sample, dispatch only on a step change.
  const dragTo = (rawCenti) => {
    if (inert || !onSetpointChange) {
      return;
    }
    const value = clampSnap(rawCenti);
    setDragValue(value);
    if (value !== lastCommittedRef.current) {
      lastCommittedRef.current = value;
      onSetpointChange(value);
    }
  };

  // A pointer event → viewBox coordinates. Returns null when the SVG cannot report
  // its transform (e.g. jsdom / no layout) so handlers degrade to no-ops.
  const toViewBox = (event) => {
    const svg = svgRef.current;
    if (
      !svg ||
      typeof svg.getScreenCTM !== 'function' ||
      typeof svg.createSVGPoint !== 'function'
    ) {
      return null;
    }
    const ctm = svg.getScreenCTM();
    if (!ctm) {
      return null;
    }
    const pt = svg.createSVGPoint();
    pt.x = event.clientX;
    pt.y = event.clientY;
    const p = pt.matrixTransform(ctm.inverse());
    return { x: p.x, y: p.y };
  };

  // A pointer angle → the temperature it represents. The angle↔temperature mapping
  // uses the FIXED arc range; dragTo then clamps the result to the zone range.
  const setFromAngle = (angle) => {
    // Ignore the bottom-gap region (matches the app's guard).
    if (angle >= 25 && angle <= 335) {
      dragTo(temperatureForAngle(angle, ARC_MIN_SETPOINT, ARC_MAX_SETPOINT));
    }
  };

  const onPointerDown = (event) => {
    if (inert) {
      return;
    }
    const p = toViewBox(event);
    if (!p) {
      return;
    }
    const dist = distanceFromCenter(p.x, p.y);
    const angle = cartesianToPolarAngle(p.x, p.y);

    if (
      dist > innerCircleRadius &&
      dist < maxDragStartDistance &&
      isNearSetpoint(angle, displaySetpoint, ARC_MIN_SETPOINT, ARC_MAX_SETPOINT)
    ) {
      drag.current.dragging = true;
      lastCommittedRef.current = setpoint;
      setDragValue(setpoint);
      // Capture the pointer so the drag keeps tracking outside the dial. We do NOT
      // preventDefault or programmatically .focus() here: the wrapper's CSS
      // (user-select: none / touch-action: none) already blocks text selection and
      // scrolling, and a scripted focus mid-gesture makes the browser briefly flash
      // the keyboard :focus-visible ring (a green circle) before it reclassifies
      // the interaction as pointer-driven. Native pointer focus does not show that
      // ring, and still leaves the dial focused so the arrow keys work after a drag.
      event.currentTarget.setPointerCapture?.(event.pointerId);
      setFromAngle(angle);
    } else if (dist < innerCircleRadius) {
      drag.current.downInside = true;
    }
  };

  const onPointerMove = (event) => {
    if (!drag.current.dragging) {
      return;
    }
    const p = toViewBox(event);
    if (p && distanceFromCenter(p.x, p.y) > innerCircleRadius) {
      setFromAngle(cartesianToPolarAngle(p.x, p.y));
    }
  };

  const onPointerUp = (event) => {
    const wasDragging = drag.current.dragging;
    const wasInside = drag.current.downInside;
    drag.current.dragging = false;
    drag.current.downInside = false;

    if (wasDragging) {
      event.currentTarget.releasePointerCapture?.(event.pointerId);
      // Drop the local value; the store's setpoint (now the last dispatched value)
      // takes over without a jump.
      setDragValue(null);
      return;
    }
    if (inert || !wasInside) {
      return;
    }
    const p = toViewBox(event);
    if (p && distanceFromCenter(p.x, p.y) < innerCircleRadius) {
      // Tap the upper half to nudge up, the lower half to nudge down.
      const angle = cartesianToPolarAngle(p.x, p.y);
      commit(setpoint + (angle >= 90 && angle <= 270 ? STEP : -STEP));
    }
  };

  const onKeyDown = (event) => {
    if (inert) {
      return;
    }
    switch (event.key) {
      case 'ArrowUp':
      case 'ArrowRight':
        commit(setpoint + STEP);
        break;
      case 'ArrowDown':
      case 'ArrowLeft':
        commit(setpoint - STEP);
        break;
      case 'Home':
        commit(minSetpoint);
        break;
      case 'End':
        commit(maxSetpoint);
        break;
      default:
        return;
    }
    event.preventDefault();
  };

  // --- geometry, all over the FIXED 5–30 arc -------------------------------
  const arcMin = ARC_MIN_SETPOINT;
  const arcMax = ARC_MAX_SETPOINT;
  const { tone, subText } = toneFor(displaySetpoint, minSetpoint);
  const isFrost = tone === 'cold';

  const clampedRoom = clampNumber(roomTemperature, arcMin, arcMax);

  // The coloured arc runs to the frost floor in frost, else up to the current room
  // level (or the target if that sits below the room).
  const arcEnd = isFrost
    ? displaySetpoint
    : Math.min(displaySetpoint, clampedRoom);
  const arcPath = pathForTemperatureArc(arcEnd, arcMin, arcMax);

  // Faint guide dots at every whole degree across the whole arc.
  const incrementDots = degreeSteps(arcMin, arcMax + 1);
  // The climbing dots bridge the room→target gap while heating (setpoint > room).
  const climbingDots =
    !isFrost && displaySetpoint > clampedRoom
      ? degreeSteps(Math.ceil(clampedRoom / 100) * 100, displaySetpoint)
      : [];

  const roomDot = getSetpointCoord(clampedRoom, arcMin, arcMax);
  const handleDot = getSetpointCoord(displaySetpoint, arcMin, arcMax);
  const minLabel = getSetpointCoord(arcMin, arcMin, arcMax);
  const maxLabel = getSetpointCoord(arcMax, arcMin, arcMax);

  return (
    <div
      className={`radial radial--tone-${tone}${
        inert ? ' radial--disabled' : ''
      }${offline ? ' radial--offline' : ''}`}
      role="slider"
      aria-label={label}
      aria-valuemin={minSetpoint / 100}
      aria-valuemax={maxSetpoint / 100}
      aria-valuenow={displaySetpoint / 100}
      // Offline: the accessible value says so rather than reading out a target
      // that is neither current nor settable, matching the visible face.
      aria-valuetext={
        offline ? 'Offline' : `${formatCelsius(displaySetpoint)}°C`
      }
      aria-disabled={inert || undefined}
      tabIndex={inert ? -1 : 0}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onKeyDown={onKeyDown}
      style={{ touchAction: 'none' }}
    >
      <svg
        ref={svgRef}
        className="radial__svg"
        viewBox="0 0 380 380"
        aria-hidden="true"
      >
        <circle
          className="radial__bg-outer"
          cx={center.x}
          cy={center.y}
          r={outerCircleRadius}
        />
        <circle
          className="radial__bg-inner"
          cx={center.x}
          cy={center.y}
          r={innerCircleRadius}
        />

        {offline ? (
          <>
            {OFFLINE_RING.map((p, i) => (
              <circle
                key={`ring-${i}`}
                className="radial__ring-dot"
                cx={p.x}
                cy={p.y}
                r="2"
              />
            ))}
            <text
              className="radial__offline"
              x={center.x}
              y={center.y + 15}
              textAnchor="middle"
            >
              Offline
            </text>
          </>
        ) : (
          <>
            {/* Faint increment dots — one per whole degree across the fixed arc. */}
            {incrementDots.map((t) => {
              const p = getSetpointCoord(t, arcMin, arcMax);
              return (
                <circle
                  key={`inc-${t}`}
                  className="radial__inc-dot"
                  cx={p.x}
                  cy={p.y}
                  r="1.5"
                />
              );
            })}

            {/* The coloured arc, up to the current room level (or the frost floor). */}
            <path className="radial__arc" d={arcPath} fill="none" />

            {/* Climbing dots bridging the room→target gap. Fainter near the room,
            fuller toward the target; the staggered animation-delay makes the pulse
            climb as a wave (see .radial__climb-dot). The group is keyed by the
            current setpoint so it RE-MOUNTS on every change — that restarts the
            finite pulse, so it re-pulses live during a drag and settles once the
            value stops changing, matching the app. */}
            <g key={`climb-${displaySetpoint}`}>
              {climbingDots.map((t, i) => {
                const p = getSetpointCoord(t, arcMin, arcMax);
                const opacity = 0.35 + (0.65 * (i + 1)) / climbingDots.length;
                return (
                  <circle
                    key={`climb-${t}`}
                    className="radial__climb-dot"
                    cx={p.x}
                    cy={p.y}
                    r="2.5"
                    style={{ opacity, animationDelay: `${i * 0.18}s` }}
                  />
                );
              })}
            </g>

            {/* Current room temperature (white). */}
            <circle
              className="radial__room"
              cx={roomDot.x}
              cy={roomDot.y}
              r={radialMarkerRadius}
            />

            {/* Target setpoint (accent / frost) — the drag handle. Enlarged when it
            coincides with the room dot so it stays visible. */}
            <circle
              className="radial__handle"
              cx={handleDot.x}
              cy={handleDot.y}
              r={
                displaySetpoint === clampedRoom
                  ? enlargedRadialMarkerRadius
                  : radialMarkerRadius
              }
            />

            {/* Number + unit are ONE centred text so the "°C" always flows right after
            the value (raised as a superscript via dy). */}
            <text
              className="radial__value"
              x={center.x}
              y={center.y + 6}
              textAnchor="middle"
            >
              {formatCelsius(displaySetpoint)}
              <tspan className="radial__unit" dy="-0.8em">
                °C
              </tspan>
            </text>
            <text
              className="radial__sub"
              x={center.x}
              y={center.y + 34}
              textAnchor="middle"
            >
              {disabled && disabledReason ? disabledReason : subText}
            </text>

            {/* The min/max labels are the fixed 5° / 30° arc extremes. */}
            <text
              className="radial__minmax"
              x={minLabel.x}
              y={minLabel.y + 20}
              textAnchor="middle"
            >
              {formatCelsius(arcMin)}°
            </text>
            <text
              className="radial__minmax"
              x={maxLabel.x}
              y={maxLabel.y + 20}
              textAnchor="middle"
            >
              {formatCelsius(arcMax)}°
            </text>

            {disabled && (
              <circle
                className="radial__overlay"
                cx={center.x}
                cy={center.y}
                r={disabledOverlayCircleRadius}
              />
            )}
          </>
        )}
      </svg>
    </div>
  );
}
